/**
 * FrequencyCapForm — Add/Edit frequency cap modal
 *
 * Modal form for creating or editing a FrequencyCapConfig.
 * Follows the same patterns as PolicyRuleForm.tsx.
 */
import { useState, useEffect } from 'react';
import { Button, Input, Label } from '@respectly/ui';
import { Switch } from '@respectly/ui';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@respectly/ui';
import { Modal } from '@respectly/ui';
import { Loader2 } from 'lucide-react';
import type {
  FrequencyCapConfig,
  Jurisdiction,
  PREChannel,
  MessagePurpose,
} from '@respectly/shared-types';
import { usePolicyStore } from '../../stores/usePolicyStore';

// ============================================
// Types
// ============================================

interface FrequencyCapFormProps {
  isOpen: boolean;
  onClose: () => void;
  cap?: FrequencyCapConfig | null;
  jurisdictions: Jurisdiction[];
}

interface FormState {
  name: string;
  jurisdictionCode: string;
  channel: PREChannel | '';
  messagePurpose: MessagePurpose | '';
  maxAttempts: number;
  periodHours: number;
  crossChannel: boolean;
  sourceRef: string;
  isActive: boolean;
}

// ============================================
// Constants
// ============================================

const CHANNEL_OPTIONS = [
  { value: '', label: 'All Channels' },
  { value: 'VOICE', label: 'Voice' },
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'Email' },
];

const PURPOSE_OPTIONS = [
  { value: '', label: 'All Purposes' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'INFORMATIONAL', label: 'Informational' },
];

function getDefaultForm(): FormState {
  return {
    name: '',
    jurisdictionCode: '',
    channel: '',
    messagePurpose: '',
    maxAttempts: 3,
    periodHours: 24,
    crossChannel: false,
    sourceRef: '',
    isActive: true,
  };
}

// ============================================
// Component
// ============================================

export function FrequencyCapForm({
  isOpen,
  onClose,
  cap,
  jurisdictions,
}: FrequencyCapFormProps) {
  const createFrequencyCap = usePolicyStore((s) => s.createFrequencyCap);
  const updateFrequencyCap = usePolicyStore((s) => s.updateFrequencyCap);
  const isCreating = usePolicyStore((s) => s.isCreating);
  const isUpdating = usePolicyStore((s) => s.isUpdating);

  const isEdit = !!cap;
  const isSaving = isCreating || isUpdating;

  const [form, setForm] = useState<FormState>(getDefaultForm());
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  useEffect(() => {
    if (cap) {
      setForm({
        name: cap.name,
        jurisdictionCode: cap.jurisdictionCode,
        channel: cap.channel ?? '',
        messagePurpose: cap.messagePurpose ?? '',
        maxAttempts: cap.maxAttempts,
        periodHours: cap.periodHours,
        crossChannel: cap.crossChannel,
        sourceRef: cap.sourceRef ?? '',
        isActive: cap.isActive,
      });
    } else {
      setForm(getDefaultForm());
    }
    setErrors({});
  }, [cap, isOpen]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.jurisdictionCode) newErrors.jurisdictionCode = 'Jurisdiction is required';
    if (form.maxAttempts < 1) newErrors.maxAttempts = 'Must be at least 1';
    if (form.periodHours < 1) newErrors.periodHours = 'Must be at least 1 hour';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const dto = {
      name: form.name.trim(),
      jurisdictionCode: form.jurisdictionCode,
      channel: (form.channel || null) as PREChannel | null,
      messagePurpose: (form.messagePurpose || null) as MessagePurpose | null,
      maxAttempts: form.maxAttempts,
      periodHours: form.periodHours,
      crossChannel: form.crossChannel,
      sourceRef: form.sourceRef.trim() || null,
      isActive: form.isActive,
    };

    if (isEdit && cap) {
      const success = await updateFrequencyCap(cap.id, dto);
      if (success) onClose();
    } else {
      const id = await createFrequencyCap(dto);
      if (id) onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title={isEdit ? `Edit Frequency Cap: ${cap?.name}` : 'Add Frequency Cap'}
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* Name */}
        <div className="space-y-1.5">
          <Label htmlFor="fc-name">Name *</Label>
          <Input
            id="fc-name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Florida 3/24h Cap"
          />
          {errors.name && (
            <p className="text-xs text-red-500">{errors.name}</p>
          )}
        </div>

        {/* Jurisdiction */}
        <div className="space-y-1.5">
          <Label>Jurisdiction *</Label>
          <Select
            value={form.jurisdictionCode}
            onValueChange={(v) => setForm((p) => ({ ...p, jurisdictionCode: v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select jurisdiction" />
            </SelectTrigger>
            <SelectContent>
              {jurisdictions.map((j) => (
                <SelectItem key={j.code} value={j.code}>
                  {j.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.jurisdictionCode && (
            <p className="text-xs text-red-500">{errors.jurisdictionCode}</p>
          )}
        </div>

        {/* Channel + Purpose */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Channel</Label>
            <Select
              value={form.channel}
              onValueChange={(v) =>
                setForm((p) => ({ ...p, channel: (v === '_all' ? '' : v) as PREChannel | '' }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All Channels" />
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || '_all'} value={opt.value || '_all'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Message Purpose</Label>
            <Select
              value={form.messagePurpose}
              onValueChange={(v) =>
                setForm((p) => ({
                  ...p,
                  messagePurpose: (v === '_all' ? '' : v) as MessagePurpose | '',
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All Purposes" />
              </SelectTrigger>
              <SelectContent>
                {PURPOSE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || '_all'} value={opt.value || '_all'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Max Attempts + Period Hours */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="fc-max">Max Attempts *</Label>
            <Input
              id="fc-max"
              type="number"
              min={1}
              value={form.maxAttempts}
              onChange={(e) =>
                setForm((p) => ({ ...p, maxAttempts: Number(e.target.value) || 1 }))
              }
            />
            {errors.maxAttempts && (
              <p className="text-xs text-red-500">{errors.maxAttempts}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fc-period">Period (hours) *</Label>
            <Input
              id="fc-period"
              type="number"
              min={1}
              value={form.periodHours}
              onChange={(e) =>
                setForm((p) => ({ ...p, periodHours: Number(e.target.value) || 1 }))
              }
            />
            {errors.periodHours && (
              <p className="text-xs text-red-500">{errors.periodHours}</p>
            )}
          </div>
        </div>

        {/* Cross-Channel */}
        <div className="flex items-center justify-between py-2">
          <div>
            <Label>Cross-Channel</Label>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Count attempts across all channels (not just the target channel)
            </p>
          </div>
          <Switch
            checked={form.crossChannel}
            onCheckedChange={(checked) =>
              setForm((p) => ({ ...p, crossChannel: !!checked }))
            }
          />
        </div>

        {/* Source Reference */}
        <div className="space-y-1.5">
          <Label htmlFor="fc-source">Source Reference</Label>
          <Input
            id="fc-source"
            value={form.sourceRef}
            onChange={(e) => setForm((p) => ({ ...p, sourceRef: e.target.value }))}
            placeholder="Legal citation (optional)"
          />
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between py-2">
          <Label>Active</Label>
          <Switch
            checked={form.isActive}
            onCheckedChange={(checked) =>
              setForm((p) => ({ ...p, isActive: !!checked }))
            }
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button variant="outline" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isEdit ? 'Update Cap' : 'Create Cap'}
        </Button>
      </div>
    </Modal>
  );
}
